<template>
  <div class="feed-file-preview w-100">
    <!-- UPLOADED CONTENT -->
    <template v-if="subscription_status === true">
      <div class="uploaded-content w-100">
        <div
          class="content rounded-5 pointer w-100"
          v-for="(file, index) in files"
          :key="index"
          @click="openDocumentViewer(index)"
        >
          <div class="left">
            <!-- FILE IMAGE -->
            <div class="avatar avatar-square brand_accent">
              <img
                v-lazy="localImg(fileType(file.extension))"
                alt
                class="avatar-img"
              />
            </div>

            <!-- FILE INFO -->
            <div class="file-info mr-3">
              <div class="file-title brand_primary text-left">
                {{ file.title | shortenFileName }}
              </div>
              <div class="file-meta color_grey_dark">
                {{ file.extension | toUppercase }}
                {{ file.filesize }}
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="right" v-if="false">
            <div
              class="avatar avatar_sm white pointer border hint--primary hint--top hint--rounded"
              aria-label="Preview"
              style="overflow: unset"
            >
              <span
                @click.self="openDocumentViewer(index)"
                class="icon-eye brand_primary font-18 flex-center"
              ></span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="uploaded-content w-100" title="Access Locked">
        <div
          class="file-lock rounded-5 pointer w-100"
          v-for="(file, index) in files"
          :key="index"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div
              class="left d-flex justify-content-start align-items-center nowrap"
            >
              <!-- FILE IMAGE -->
              <div class="avatar avatar-square brand_accent">
                <img
                  v-lazy="localImg(fileType(file.extension))"
                  alt
                  class="avatar-img"
                />
              </div>

              <!-- FILE INFO -->
              <div class="file-info ml-1 mr-2">
                <div class="file-title brand_primary text-left">
                  {{ file.title | shortenLockFileName }}
                </div>
                <div class="file-meta color_grey_dark">
                  {{ file.extension | toUppercase }}
                  {{ file.filesize }}
                </div>
              </div>
            </div>

            <div class="right">
              <div class="brand_tonic text-right font-weight-bold">
                <span class="icon-lock-fill font-15"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- UPLOADED CONTENT -->

    <LibraryViewer
      v-if="show_viewer"
      file_type="document"
      :file_data="getDocumentData"
      @closeTriggered="toggleViewer"
    ></LibraryViewer>

    <ResponseDialog
      v-if="response_dialog"
      :alert_msg="alert_card_msg"
      :alert_type="alert_card_type"
      @closeAlert="toggleResponseDialog"
    ></ResponseDialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { formatDate, formatTime } from "@/scripts/utilities";

export default {
  name: "FeedAttachmentPreview",

  components: {
    ResponseDialog: () =>
      import(
        /* webpackChunkName: "ResponseDialog" */ "@/components/globalComps/ResponseDialog"
      ),

    LibraryViewer: () =>
      import(
        /* webpackChunkName: "LibraryViewer" */ "@/views/globalViews/LibraryViewer"
      ),
  },

  props: {
    files: Array,
    subscription_status: Boolean,
  },

  data() {
    return {
      show_viewer: false,
      alert_card_msg: "",
      alert_card_type: "",
      response_dialog: false,
      getDocumentData: {},
    };
  },

  filters: {
    toUppercase(val) {
      return val.toUpperCase();
    },

    shortenFileName(val) {
      if (val.length > 32) return `${val.slice(0, 32)}...`;
      else return val;
    },

    shortenLockFileName(val) {
      if (val.length > 20) return `${val.slice(0, 20)}...`;
      else return val;
    },
  },

  methods: {
    ...mapActions(["downloadDocument"]),

    toggleViewer() {
      this.show_viewer = !this.show_viewer;
    },

    toggleResponseDialog() {
      this.response_dialog = !this.response_dialog;
    },

    fileType(ext) {
      let fileExt = ext;
      switch (fileExt) {
        case "pdf":
          fileExt = "PDFFILE.svg";
          break;
        case "ppt":
          fileExt = "PPTFILE.svg";
          break;
        case "pptx":
          fileExt = "PPTFILE.svg";
          break;
        case "doc":
          fileExt = "DOCFILE.svg";
          break;
        case "docx":
          fileExt = "DOCXFILE.svg";
          break;
        case "xls":
          fileExt = "XLSFILE.svg";
          break;
        case "xlsx":
          fileExt = "XLSFILE.svg";
          break;
        case "txt":
          fileExt = "TxtFile.svg";
          break;
        case "epub":
          fileExt = "DOCEPUB.png";
          break;
        case "mp4":
          fileExt = "VIDMP4.png";
          break;
        default:
          fileExt = "PDFFILE.svg";
          break;
      }
      return fileExt;
    },

    formatDocumentDate(date) {
      return formatDate(date);
    },

    formatDocumentTime(date) {
      return formatTime(date);
    },

    openDocumentViewer(index) {
      this.getDocumentData.title = this.files[index].title;
      this.getDocumentData.url = this.files[index].filename;
      this.getDocumentData.token = this.files[index].token;
      this.getDocumentData.extension = this.files[index].extension;
      this.getDocumentData.date = this.formatDocumentDate(
        this.files[index].created_at
      );
      this.getDocumentData.time = this.formatDocumentTime(
        this.files[index].created_at
      );
      this.getDocumentData.fullname = this.files[index].user.full_name;
      this.getDocumentData.image = this.files[index].user.image;

      this.toggleViewer();
    },

    openDocument(index) {
      let a = document.createElement("a");
      a.target = "_blank";
      a.href = `https://docs.google.com/viewer?url=${this.files[index].filename}&embedded=true\n`;
      a.click();
    },

    downloadFile(index) {
      this.downloadDocument(this.files[index].token)
        .then((response) => {
          let data = response.data;
          if (data.filename) {
            this.alert_card_msg = "Downloading file...";
            this.alert_card_type = "warning";
            this.toggleResponseDialog();

            // FETCH FILE DETAIL
            fetch(data.filename).then((t) => {
              return t.blob().then((b) => {
                let a = document.createElement("a");
                a.href = URL.createObjectURL(b);
                a.setAttribute("download", data.title + "." + data.extension);
                a.click();
              });
            });
          }
        })
        .catch((err) => console.log(err));
    },
  },
};
</script>

<style lang="scss" scoped>
.feed-file-preview {
  position: relative;
  left: -8px;
  margin-top: 20px;

  @include breakpoint_max(sm) {
    margin-left: 4px;
  }

  @include breakpoint_max(xs) {
    width: 90%;
    margin-right: 10%;
  }

  .uploaded-content {
    margin-top: 10px;

    .content {
      background: $color_white;
      @include flex_row_between_nowrap;
      border: 1px solid $border_grey;
      padding: 10px;
      margin-bottom: 8px;
      transition: all 0.4s ease-in-out;
      transform: scale(1.01);

      @include breakpoint_max(sm) {
        padding: 7px;
      }

      .left {
        @include flex_row_start_nowrap;

        .avatar {
          @include equal_min_edge(34);
          margin-right: 10px;

          @include breakpoint_max(xs) {
            margin-right: 6px;
          }

          img {
            @include transform_center;
            @include equal_min_edge(32);

            @include breakpoint_max(xs) {
              @include equal_min_edge(28);
            }
          }
        }

        .file-title {
          font-size: 13.5px;
          line-height: 19px;
          margin-bottom: 5px;
          word-break: break-all;
          text-overflow: ellipsis;
          overflow-wrap: break-word;
          word-wrap: break-word;
          hyphens: auto;

          @include breakpoint_max(sm) {
            font-size: 12.4px;
            line-height: 15px;
          }

          @include breakpoint_max(xs) {
            font-size: 11.5px;
          }
        }

        .file-meta {
          font-size: 12px;
          line-height: 16px;

          @include breakpoint_max(xs) {
            font-size: 10px;
            line-height: 13px;
          }
        }
      }
      .right {
        .avatar {
          @include equal_min_edge(33);
          span {
            transition: all 0.3s ease-in-out;
          }
          &:hover {
            span {
              color: $brand_inverse !important;
            }
          }
        }
      }
    }
  }

  .file-lock {
    border: 1px solid $brand_red;
    border-radius: 30px;
    cursor: not-allowed !important;
    background: $brand_red_light;
    padding: 10px 20px;
    position: relative;
  }
}
</style>
